import React from 'react';
import Card, { CardContent } from '../../components/ui/Card';

const TermsOfServicePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
            <p className="text-sm text-gray-600 mb-8">Last Updated: {new Date().toLocaleDateString()}</p>

            <div className="prose prose-gray max-w-none">
              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">1. Agreement to Terms</h2>
              <p className="text-gray-700 mb-4">
                By accessing or using Patreon Clone, you agree to be bound by these Terms of Service and all applicable 
                laws and regulations. If you do not agree with any of these terms, you are prohibited from using this platform.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">2. Description of Service</h2>
              <p className="text-gray-700 mb-4">
                Patreon Clone is a membership platform that enables creators to receive recurring financial support from 
                patrons in exchange for exclusive content, benefits, and community access.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">3. User Accounts</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.1 Account Creation</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>You must be at least 18 years old to create an account</li>
                <li>You must provide accurate and complete information</li>
                <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                <li>You are responsible for all activities under your account</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.2 Account Termination</h3>
              <p className="text-gray-700 mb-4">
                We reserve the right to suspend or terminate your account at any time for violations of these Terms, 
                including but not limited to fraudulent activity, violation of our Acceptable Use Policy, or harmful behavior.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">4. Creator Terms</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.1 Creator Responsibilities</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>You must deliver promised benefits and content to patrons</li>
                <li>You must comply with all applicable laws and regulations</li>
                <li>You are responsible for all taxes related to your creator earnings</li>
                <li>You must maintain accurate payout information</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.2 Content Ownership</h3>
              <p className="text-gray-700 mb-4">
                You retain ownership of all content you create and post. By posting content, you grant us a non-exclusive, 
                worldwide, royalty-free license to use, display, and distribute your content on our platform.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.3 Platform Fees</h3>
              <p className="text-gray-700 mb-4">
                We charge a platform fee of 20% on all payments from patrons. Payment processing fees (set by Stripe) 
                are additional. Fees are clearly disclosed during account setup.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">5. Patron Terms</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">5.1 Subscriptions and Payments</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Subscriptions are recurring and charged monthly unless cancelled</li>
                <li>You authorize us to charge your payment method at each billing cycle</li>
                <li>Cancellations take effect at the end of the current billing period</li>
                <li>Refunds are at the discretion of the creator and platform</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">5.2 Access to Content</h3>
              <p className="text-gray-700 mb-4">
                Access to creator content is contingent on maintaining an active subscription at the appropriate tier. 
                Creators may modify tier benefits with reasonable notice.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">6. Prohibited Conduct</h2>
              <p className="text-gray-700 mb-4">You agree not to:</p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Post illegal, harmful, threatening, or abusive content</li>
                <li>Infringe on intellectual property rights</li>
                <li>Engage in harassment or hate speech</li>
                <li>Use the platform for fraudulent purposes</li>
                <li>Attempt to circumvent payment systems</li>
                <li>Share login credentials with others</li>
                <li>Use automated systems to scrape content</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">7. Content Moderation</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to review, moderate, and remove content that violates these Terms or our Acceptable 
                Use Policy. We are not responsible for user-generated content but will take action against violations when notified.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">8. Intellectual Property</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">8.1 Platform IP</h3>
              <p className="text-gray-700 mb-4">
                All platform features, design, code, and trademarks are owned by Patreon Clone and protected by 
                intellectual property laws.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">8.2 DMCA Compliance</h3>
              <p className="text-gray-700 mb-4">
                We comply with the Digital Millennium Copyright Act. If you believe your copyright has been infringed, 
                contact our DMCA agent at dmca@patreonclone.com.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">9. Disclaimers and Limitations</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">9.1 Service Availability</h3>
              <p className="text-gray-700 mb-4">
                We provide the platform "as is" without warranties of any kind. We do not guarantee uninterrupted 
                access or error-free operation.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">9.2 Limitation of Liability</h3>
              <p className="text-gray-700 mb-4">
                To the maximum extent permitted by law, Patreon Clone shall not be liable for any indirect, incidental, 
                special, consequential, or punitive damages arising from your use of the platform.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">10. Dispute Resolution</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">10.1 Arbitration</h3>
              <p className="text-gray-700 mb-4">
                Any disputes arising from these Terms will be resolved through binding arbitration, except where 
                prohibited by law. You waive the right to participate in class action lawsuits.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">10.2 Governing Law</h3>
              <p className="text-gray-700 mb-4">
                These Terms are governed by the laws of [Your Jurisdiction], without regard to conflict of law principles.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">11. Indemnification</h2>
              <p className="text-gray-700 mb-4">
                You agree to indemnify and hold harmless Patreon Clone from any claims, damages, or expenses arising 
                from your use of the platform or violation of these Terms.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">12. Changes to Terms</h2>
              <p className="text-gray-700 mb-4">
                We may modify these Terms at any time. Material changes will be communicated via email or platform 
                notice. Continued use after changes constitutes acceptance.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">13. Severability</h2>
              <p className="text-gray-700 mb-4">
                If any provision of these Terms is found to be unenforceable, the remaining provisions will continue 
                in full force and effect.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">14. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                For questions about these Terms, contact us at:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-gray-700"><strong>Email:</strong> legal@patreonclone.com</p>
                <p className="text-gray-700"><strong>Address:</strong> [Your Company Address]</p>
              </div>

              <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Important:</strong> By clicking "I Agree" or by using this platform, you acknowledge that 
                  you have read, understood, and agree to be bound by these Terms of Service.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
